import { Injectable } from '@angular/core';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { GeoJsonUtilityService } from 'src/app/modules/cop/drawing/geo-json-utility.service';

const DEFAULT_GEOMETRY_FIELD = 'geometry';

@Injectable({
  providedIn: 'root'
})
export class FeatureRenderingUtilityService {
  static getDocumentWorkflowColor(dataset: Dataset, document: Document): string {
    const workflow = dataset.workflow;
    const workflowStepUId = document.workflow_step_uid;
    const workflowStep = workflow.workflowSteps.find(wfs => wfs.uId === workflowStepUId);
    return workflowStep?.statusHtmlColorCode;
  }

  static getGeometryFieldSystemName(dataset: Dataset, layer: Layer): string {
    const geoFieldName =
      layer.displayOptions?.geoFieldsToDisplay?.length > 0 ? layer.displayOptions.geoFieldsToDisplay[0] : DEFAULT_GEOMETRY_FIELD;

    let geoField = dataset.schema.fields.find(f => f.systemName === geoFieldName);
    if (!!!geoField) {
      geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Geometry);
    }

    return geoField?.systemName;
  }

  static getExtraGeometryFieldSystemName(dataset: Dataset, layer: Layer): string {
    return layer.displayOptions?.optionalGeoFieldsToDisplay?.length > 0 ? layer.displayOptions.optionalGeoFieldsToDisplay[0] : null;
  }

  static getGeometryFeaturesForDisplay(dataset: Dataset, layer: Layer, document: Document): any[] {
    const geoField = this.getGeometryFieldSystemName(dataset, layer);

    if (!!!geoField) {
      return;
    }

    const fieldValueJson = document[geoField];
    if (!!!fieldValueJson) {
      return;
    }

    let fieldValue: any = null;
    try {
      fieldValue = JSON.parse(fieldValueJson);
    } catch {}

    if (!!!fieldValue) {
      return;
    }

    const geoFeatures = GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(fieldValue);
    if (!!!geoFeatures) {
      return;
    }

    return geoFeatures;
  }

  static normalizePolygonCoordinates(list: [number, number][][] | [number, number][][][]): google.maps.LatLng[][] | google.maps.LatLng[] {
    // not a polygon or an empy polygon
    try {
      if (!Array.isArray(list) || !Array.isArray(list[0]) || list[0].length === 0 || typeof list[0][0] === 'number') {
        return [] as google.maps.LatLng[];
      }
      // [number, number][][]
      if (
        Array.isArray(list) &&
        list.length &&
        Array.isArray(list[0]) &&
        list[0].length &&
        typeof (list as [number, number][][])[0][0][0] === 'number'
      ) {
        return (list as [number, number][][]).map(polyRing => {
          const newPoly = (polyRing as [number, number][]).map(coord => {
            const newCoord = this.extractLatLng(coord);
            return newCoord;
          });
          return newPoly;
        }) as google.maps.LatLng[][];
      }

      // [number, number][][][]
      const arr = list as [number, number][][][];
      const newPolys = arr.map(polyRings => {
        const newPoly = (polyRings[0] as [number, number][]).map(coord => {
          const newCoord = this.extractLatLng(coord);
          return newCoord;
        });
        return newPoly;
      });
      return newPolys as google.maps.LatLng[][];
    } catch (e) {
      return [] as google.maps.LatLng[];
    }
  }

  static extractLatLng(array: [number, number]): google.maps.LatLng {
    if (array.length > 1) {
      return new google.maps.LatLng(array[1], array[0]);
    } else {
      return new google.maps.LatLng(0, 0);
    }
  }
}
